/* eslint-disable react-hooks/exhaustive-deps */
import classNames from 'classnames';
import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ForwardRefContext from 'src/contexts/ForwardRefContext';
import { ContentPane } from './ContentPane';
import {
  useSession,
  useAuthUser,
  useColorTheme,
  useSocket,
  useQueryParams,
  useTermsServiceAgreement,
  usePageTracking,
  useAuthRedirectForSpecialRoutes,
  useTeamInfo,
  useUTMMetrics,
} from 'src/hooks';
import { Loading } from 'src/components/Loading';
import { PrivateContent } from 'src/components/PrivateContent';
import { PublicContent } from 'src/components/PublicContent';
import AuthContext from 'src/contexts/AuthContext';
import { Banner } from 'src/components/Banner';
import {
  COGNITO_FACEBOOK_EMAIL_NOT_SHARED_ERROR_MARKER,
  COGNITO_PRE_SIGNUP_ERROR_PREFIX,
} from 'src/constants/cognito';
import { isEnterprise } from 'src/utils';
import { APINestedRoutes, AppRoutes, AuthRoutes } from 'src/types';
import { Toast } from './Toast';
import Cookies from 'js-cookie';

const SUPER_NINJA_RETURN_URL_COOKIE_NAME = '_superNinjaReturnUrl';

const allowedRoutesForDarkTheme = [
  AppRoutes.HOME,
  AppRoutes.VIDEO_TUTORIALS,
  `${AppRoutes.ADD_ON}/${APINestedRoutes.CREDITS}`,
  `${AppRoutes.ADD_ON}/${APINestedRoutes.API_KEYS}`,
  `${AppRoutes.ADD_ON}/${APINestedRoutes.API_USAGE}`,
];

const App = () => {
  const location = useLocation();
  const { rootContainerRef } = useContext(ForwardRefContext);
  const { searchParams } = useQueryParams();
  const { isUserLoading, fetchUserInfo } = useAuthUser();
  const { appUser } = useSession();
  const { isDarkTheme } = useColorTheme();
  const { isTeamInfoLoading } = useTeamInfo();
  useSocket();
  useTermsServiceAgreement();
  usePageTracking();
  useAuthRedirectForSpecialRoutes();
  useUTMMetrics();

  const {
    isGuestAccess,
    shouldFetchUser,
    isAuthenticated,
    isAuthLoading,
    userId: authUserId,
  } = useContext(AuthContext);

  useEffect(() => {
    // Only for Chrome-based browsers. "stale issue" might not happen in other browsers because they use different approach for resource optimization.
    const resumeHandler = (event: unknown) => {
      console.debug('resume', event);
      // re-fetch initial data and reconnect to WS: should be enough to fix "stale issue"
      window.location.reload();
    };
    document.addEventListener('resume', resumeHandler);

    return () => {
      document.removeEventListener('resume', resumeHandler);
    };
  }, []);

  useEffect(() => {
    // load user
    if (!isAuthLoading && authUserId && shouldFetchUser) {
      fetchUserInfo(authUserId);
    }
  }, [isAuthLoading, authUserId, shouldFetchUser]);

  // cognito sign-in error
  const isSignError = searchParams.error && searchParams.error_description;

  useEffect(() => {
    if (isSignError) {
      if (
        searchParams.error_description?.startsWith(
          COGNITO_FACEBOOK_EMAIL_NOT_SHARED_ERROR_MARKER,
        )
      ) {
        window.location.replace(
          `/sign-up?signup_error=${searchParams.error_description?.replace(
            COGNITO_PRE_SIGNUP_ERROR_PREFIX,
            '',
          )}`,
        );
      } else {
        const superNinjaReturnUrl = Cookies.get(
          SUPER_NINJA_RETURN_URL_COOKIE_NAME,
        );
        if (superNinjaReturnUrl) {
          Cookies.remove(SUPER_NINJA_RETURN_URL_COOKIE_NAME, {
            domain: window.location.hostname,
          });
          const errorMessage =
            searchParams.error_description?.replace(
              COGNITO_PRE_SIGNUP_ERROR_PREFIX,
              '',
            ) || 'An error occurred during sign in.';
          try {
            const returnUrl = new URL(superNinjaReturnUrl);
            returnUrl.searchParams.set('auth_error', errorMessage);
            window.location.replace(returnUrl.toString());
          } catch {
            window.location.replace('/');
          }
        } else {
          window.location.replace('/');
        }
      }
    }
  }, [isSignError]);

  const isScreenWithAllowedDarkMode =
    isAuthenticated ||
    (!isAuthenticated &&
      (location.pathname === '/' ||
        (allowedRoutesForDarkTheme as string[]).includes(location.pathname))) ||
    isAuthLoading ||
    isUserLoading; // todo temporary workaround until the auth screens have a dark mode design.

  const isAuthRoute = AuthRoutes.includes(location.pathname as AppRoutes);

  return !isUserLoading &&
    !isAuthLoading &&
    !isSignError &&
    (appUser.user_id || isGuestAccess) &&
    !isTeamInfoLoading ? (
    <div
      ref={rootContainerRef}
      className={classNames('nj-theme', {
        'dark-mode': isDarkTheme && isScreenWithAllowedDarkMode,
      })}
    >
      <ContentPane withRightSideBar={!isAuthRoute}>
        {/* for Enterprise, we don't show up banners in public content */}
        {!isEnterprise && <Banner />}
        {isAuthenticated ? <PrivateContent /> : <PublicContent />}

        <Toast />
      </ContentPane>
    </div>
  ) : (
    <ContentPane>
      <Loading />
    </ContentPane>
  );
};

export default App;
